import React, { type ReactElement, useRef } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import useVideoPlaybackTracking from '@hooks/useVideoPlaybackTracking';
import { Page } from '@tracking';

const VIDEO_URL = 'https://tii.imgix.net/global/jobs/the-information-job-page-video.mp4';
const VIDEO_IMG_POSTER_URL =
  'https://tii.imgix.net/global/jobs/the-information-job-page-video-poster.jpg?w=604&dpi=2&format=auto';

interface HeaderProps {
  onButtonClick: () => void;
}

const Header = ({ onButtonClick }: HeaderProps): ReactElement => {
  const { Track } = useEventTracking({
    page: Page.Jobs,
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const { handleVideoEnd, handleVideoPause, handleVideoPlay, throttledHandleVideoTimeUpdate } =
    useVideoPlaybackTracking({ src: VIDEO_URL, videoRef });

  return (
    <section className="mb-72 grid items-center gap-64 sm:mb-80 sm:gap-36 md:mb-[140px] md:grid-cols-2 md:gap-48 lg:mb-[180px]">
      <Track>
        <div>
          <span className="mb-12 font-suisse-intl-mono text-overline-md text-primary-magenta">
            Jobs Center
          </span>
          <h1 className="mb-8 mt-0 font-suisse-works text-headline-sm text-primary-black sm:text-headline-xl">
            Come work with us
          </h1>
          <p className="mb-24 mt-0 font-suisse-neue text-body-md text-primary-black sm:text-body-lg">
            The Information is seeking individuals passionate about the future of the news business
            for the roles below.
          </p>
          <PrimaryButton as="button" onClick={onButtonClick}>
            View job openings
          </PrimaryButton>
        </div>
        <div className="aspect-[16/9]">
          <video
            className="h-full w-full"
            onEnded={handleVideoEnd}
            onPause={handleVideoPause}
            onPlay={handleVideoPlay}
            onTimeUpdate={throttledHandleVideoTimeUpdate}
            poster={VIDEO_IMG_POSTER_URL}
            preload="metadata"
            ref={videoRef}
            src={VIDEO_URL}
            controls
            playsInline
          />
        </div>
      </Track>
    </section>
  );
};

export default Header;
